<template>
  <div class="signup-page">
    <h1>PathTracer Sign Up</h1>

      <!-- Global error banner, visible in every phase -->
    <p v-if="errorMessage" class="error-message global-error">
      {{ errorMessage }}
    </p>

    <div class="phase-stack">

      <!-- INTRO CARD -->
      <div class="phase-card" :class="getPhaseClass('intro')">
        <div
          class="step-header"
          :class="{ clickable: getPhaseClass('intro') === 'complete-phase' }"
          @click="trySetPhase('intro')"
        >
          <span>1</span>
          <label>Intro</label>
        </div>
        <div v-if="currentPhase === 'intro'">
          <p>Welcome to PathTracer! This platform provides advanced blockchain transaction visualizations and fund flow analysis.</p>
          <p>Let’s walk through a short signup process to create your account.</p>
          <button
            class="primary-button"
            @click="currentPhase = 'form'"
            data-track="signup_start_click"
            :data-track-props="JSON.stringify({ phase_from: currentPhase })"
          >
            Start Signup
          </button>
        </div>
      </div>

      <!-- FORM CARD (DETAILS) -->
      <div class="phase-card" :class="getPhaseClass('form')">
        <div
          class="step-header"
          :class="{ clickable: getPhaseClass('form') === 'complete-phase' }"
          @click="trySetPhase('form')"
        >
          <span>2</span>
          <label>Details</label>
        </div>
        <div v-if="currentPhase === 'form'">
          <form @submit.prevent="goToAccount">
            <div class="form-group" v-for="field in formFields" :key="field.label">
              <label :for="field.model">{{ field.label }}</label>

              <!-- DOB -> flatpickr-enhanced input -->
              <input
                v-if="field.model === 'dob'"
                id="dob"
                ref="dobInput"
                type="text"
                data-1p-ignore
                class="datepicker-input"
                :required="field.required"
                placeholder="YYYY-MM-DD"
              />

              <!-- Country -> select, with NA countries at top -->
              <select
                v-else-if="field.model === 'country'"
                v-model="formModel.country"
                id="country"
                name="country"
                :required="field.required"
                size="8"
                class="country-select"
              >
                <option disabled value="">Select your country</option>

                <option
                  v-for="country in countries"
                  :key="country.code"
                  :value="country.name"
                >
                  {{ country.name }}
                </option>
              </select>

              <!-- Intended Use select -->
              <select
                v-else-if="field.select"
                v-model="formModel.intendedUse"
                id="intendedUse"
                name="intendedUse"
              >
                <option disabled value="">Please choose</option>
                <option>Investigation</option>
                <option>Education</option>
                <option>Personal Curiosity</option>
                <option>I am the victim of a scam</option>
                <option>Other</option>
              </select>

              <!-- All other non-select fields -->
              <input
                v-else
                v-model.trim="formModel[field.model]"
                :type="field.type"
                :required="field.required"
                :maxlength="field.max"
                :id="field.model"
                data-1p-ignore
                :name="field.model"
              />
            </div>

        <div class="form-group">
            
<label class="checkbox-label">
  <input type="checkbox" v-model="formModel.lawEnforcementFlag" />
  <span>I represent a law enforcement agency or legal entity</span>
</label>

</div>


            <div class="form-group">
              <label>Referral Code (optional):</label>
              <input
                v-model.trim="formModel.referralCode"
                type="text"
                data-1p-ignore
                maxlength="64"
                :readonly="referralFromQuery"
              />
              <small v-if="referralFromQuery" class="helper">
                Referral code applied from your link
              </small>
            </div>

            <!-- Promo Code (optional) -->
            <!--
            <div class="form-group">
              <label>Promo Code (optional):</label>
              <input v-model.trim="formModel.promoCode" type="text" maxlength="50" />
            </div>
            -->

            <button
              class="primary-button"
              type="submit"
              data-track="signup_next_account_click"
              :data-track-props="JSON.stringify({
                hasReferral: !!formModel.referralCode,
                law: !!formModel.lawEnforcementFlag
              })"
            >
              Next: Create Account
            </button>
            <button class="secondary-button" type="button" @click="currentPhase = 'intro'">
              Back
            </button>
          </form>
        </div>
      </div>

      <!-- ACCOUNT CARD (USERNAME + PASSWORD) -->
      <div class="phase-card" :class="getPhaseClass('account')">
        <div
          class="step-header"
          :class="{ clickable: getPhaseClass('account') === 'complete-phase' }"
          @click="trySetPhase('account')"
        >
          <span>3</span>
          <label>Account</label>
        </div>
        <div v-if="currentPhase === 'account'">
          <form @submit.prevent="goToTOSFromAccount">
            <div class="form-group">
              <label for="username">Choose Username:</label>
              <input
                id="username"
                v-model.trim="username"
                type="text"
                data-1p-ignore
                required
                maxlength="50"
              />
              <small class="helper">
                Username must start with a letter and contain only letters, numbers,
                underscores, or hyphens.
              </small>
            </div>

            <div class="form-group">
              <label for="password">Choose Password:</label>
              <input
                id="password"
                v-model.trim="password"
                type="password"
                required
                minlength="6"
              />
            </div>

            <div class="form-group">
              <label for="passwordConfirm">Confirm Password:</label>
              <input
                id="passwordConfirm"
                v-model.trim="passwordConfirm"
                type="password"
                required
                minlength="6"
              />
            </div>

            <button
              class="primary-button"
              type="submit"
              data-track="signup_next_terms_click"
              :data-track-props="JSON.stringify({ from: 'account' })"
            >
              Next: Terms of Service
            </button>
            <button
              class="secondary-button"
              type="button"
              @click="currentPhase = 'form'"
            >
              Back
            </button>
          </form>
        </div>
      </div>

      <!-- TOS CARD -->
      <div class="phase-card" :class="getPhaseClass('tos')">
        <div
          class="step-header"
          :class="{ clickable: getPhaseClass('tos') === 'complete-phase' }"
          @click="trySetPhase('tos')"
        >
          <span>4</span>
          <label>Agreement</label>
        </div>
        <div v-if="currentPhase === 'tos'">
          <p>Please read and agree to the following before creating your account:</p>

          <div class="tos-buttons">
            <button
              class="tertiary-button"
              type="button"
              @click="showTermsModal = true"
              data-track="signup_open_terms"
            >
              View Terms of Service
            </button>
            <button
              class="tertiary-button"
              type="button"
              @click="showPrivacyModal = true"
              data-track="signup_open_privacy"
            >
              View Privacy Policy
            </button>
            <button
              class="tertiary-button"
              type="button"
              @click="showFairUseModal = true"
              data-track="signup_open_fair_use"
            >
              View Fair Use Policy
            </button>
          </div>

          <label>
            <input type="checkbox" v-model="agreedToTerms" />
            I agree to the Terms of Service, Privacy Policy and Fair Use Policy.
          </label>

          <div style="margin-top: 20px;">
            <button
              class="primary-button"
              @click="submitSignup"
              :disabled="isSubmitting"
              data-track="signup_submit_click"
              :data-track-props="JSON.stringify({
                agreed: !!agreedToTerms
              })"
            >
              <span v-if="!isSubmitting">Create Account</span>
              <span v-else class="spinner-with-text">
                <span class="spinner"></span> Creating account...
              </span>
            </button>
            <button
              class="secondary-button"
              type="button"
              @click="currentPhase = 'account'"
            >
              Back
            </button>
          </div>

          <p v-if="errorMessage" class="error-message">{{ errorMessage }}</p>
          <p v-if="successMessage" class="success-message">{{ successMessage }}</p>
        </div>
      </div>
<!-- Terms Modal -->
<div v-if="showTermsModal" class="modal-overlay" @click.self="showTermsModal = false">
  <div class="modal">
    <h2 class="modal-title">Terms of Service</h2>
    <div class="modal-content formatted-tos">
      <p><strong>Last Updated:</strong> June 28, 2025</p>

      <p>
        Please read these Terms of Service ("Terms") carefully before using the Pathtracer platform ("Service"). Your access to and use of the Service is conditioned on your acceptance of and compliance with these Terms. By accessing or using the Service, you agree to be bound by these Terms.
      </p>

      <hr />

      <h3>1. Description of Service</h3>
      <p>
        Pathtracer provides advanced blockchain transaction visualizations and fund flow analysis. The Service is intended primarily for investigative, educational, and analytical purposes, assisting users in tracing cryptocurrency transactions.
      </p>

      <h3>2. Eligibility</h3>
      <ul>
        <li>Are at least 18 years of age.</li>
        <li>Have the legal capacity and authority to enter into these Terms.</li>
        <li>Will use the Service in compliance with all applicable laws and regulations.</li>
      <li>Within US or Canada</li>
      </ul>

      <h3>3. User Accounts and Access</h3>
      <p>You agree to:</p>
      <ul>
        <li>Provide accurate and complete information during registration.</li>
        <li>Maintain and promptly update your account information.</li>
        <li>Secure your login credentials and notify us immediately of any unauthorized access.</li>
      <li>Disable any VPNs or hosting while using the platform.</li>
      </ul>

      <h3>4. Acceptable Use</h3>
      <p>You agree not to misuse the Service, including but not limited to:</p>
      <ul>
        <li>Attempting unauthorized access to the Service.</li>
        <li>Using automated systems or scripts to extract data from Pathtracer without permission.</li>
        <li>Violating applicable laws or regulations.</li>
        <li>Engaging in activities that disrupt or interfere with the functionality or security of Pathtracer.</li>
      </ul>

      <h3>5. Law Enforcement and Legal Entities</h3>
      <p>
        If you represent a law enforcement agency or legal entity, you must accurately disclose this at registration. Pathtracer cooperates with valid legal requests from authorities, complying with applicable laws.
      </p>

      <h3>6. Data Privacy</h3>
      <p>
        Your privacy is important to us. Please review our
        <a href="/blockchaintools/privacy" target="_blank">Privacy Policy</a> to understand how we collect, use, and safeguard your information.
      </p>

      <h3>7. Limitation of Liability</h3>
      <p>
        Pathtracer provides blockchain data visualization and analytical insights on an "as-is" basis. While we strive for accuracy, we do not guarantee the completeness or accuracy of information provided. Pathtracer shall not be liable for any direct, indirect, or consequential losses arising from your use of the Service.
      </p>

      <h3>8. Modifications to the Terms</h3>
      <p>
        We reserve the right to update or modify these Terms at any time. Changes will be posted on this page with an updated date. Your continued use of the Service following any modifications indicates your acceptance of the new Terms.
      </p>

      <h3>9. Intellectual Property</h3>
      <p>
        All content, features, and functionality of Pathtracer, including data visualizations, reports, and software, are the exclusive property of Pathtracer and are protected by applicable intellectual property laws.
      </p>

      <h3>10. Termination</h3>
      <p>
        We reserve the right to suspend or terminate your account and access to the Service, without prior notice, for any reason, including violation of these Terms.
      </p>

      <h3>11. Governing Law</h3>
      <p>
        These Terms shall be governed by and interpreted in accordance with the laws of your jurisdiction, without regard to conflict of law principles.
      </p>

      <h3>12. Contact Information</h3>
      <p>
        For questions regarding these Terms, please contact us at
        <a href="mailto:support@pathtracer.io">support@pathtracer.io</a>.
      </p>

      <p><em>By using Pathtracer, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.</em></p>
    </div>

    <button class="primary-button" @click="showTermsModal = false">Close</button>
  </div>
</div>


<!-- Privacy Modal -->
<div v-if="showPrivacyModal" class="modal-overlay" @click.self="showPrivacyModal = false">
  <div class="modal">
    <h2 class="modal-title">Privacy Policy</h2>
    <div class="modal-content formatted-privacy">
      <p><strong>Last Updated:</strong> June 28, 2025</p>

      <p>
        This Privacy Policy explains how Pathtracer ("we", "our", or "us") collects, uses, and protects your information when you access or use our platform.
      </p>

      <hr />

      <h3>1. Information We Collect</h3>
      <ul>
        <li><strong>Registration Data:</strong> Name, email address, date of birth, country of residence, and intended use.</li>
        <li><strong>Log Data:</strong> IP address, browser type, device information, and usage timestamps.</li>
        <li><strong>Voluntary Info:</strong> Referral codes, agency affiliation, and other details you provide during signup or interaction.</li>
      </ul>

      <h3>2. How We Use Your Information</h3>
      <ul>
        <li>To create and manage user accounts.</li>
        <li>To improve service functionality and user experience.</li>
        <li>To respond to support inquiries or legal requests.</li>
        <li>To monitor suspicious activity and protect the platform from abuse.</li>
      </ul>

      <h3>3. Cookies and Analytics</h3>
      <p>
        We may use cookies and analytics tools to understand user behavior and improve our platform. You can manage cookie preferences via your browser settings.
      </p>

      <h3>4. Information Sharing</h3>
      <p>
        We do <strong>not</strong> sell or rent your personal information. We may share information with trusted service providers or authorities under the following circumstances:
      </p>
      <ul>
        <li>To comply with legal obligations or law enforcement requests.</li>
        <li>To enforce our Terms of Service or investigate potential violations.</li>
        <li>To maintain security and prevent abuse or fraud.</li>
      </ul>

      <h3>5. Data Security</h3>
      <p>
        We implement reasonable security measures to protect your personal data. However, no method of transmission over the internet is 100% secure.
      </p>

      <h3>6. Your Rights</h3>
      <ul>
        <li>You can request access to or correction of your personal data.</li>
        <li>You can request deletion of your account and associated data.</li>
        <li>We may retain limited information for legal or audit purposes.</li>
      </ul>

      <h3>7. Children's Privacy</h3>
      <p>
        Pathtracer is not intended for use by individuals under the age of 18. We do not knowingly collect personal information from children.
      </p>

      <h3>8. Updates to This Policy</h3>
      <p>
        We may update this Privacy Policy from time to time. When we do, we’ll update the date above and notify users of significant changes when applicable.
      </p>

      <h3>9. Contact Us</h3>
      <p>
        If you have any questions about this Privacy Policy, please contact us at
        <a href="mailto:support@pathtracer.com">support@pathtracer.com</a>.
      </p>

      <p><em>By using Pathtracer, you consent to the collection and use of your information as described in this Privacy Policy.</em></p>
    </div>

    <button class="primary-button" @click="showPrivacyModal = false">Close</button>
  </div>
</div>

<!-- Fair Use Modal -->
<div v-if="showFairUseModal" class="modal-overlay" @click.self="showFairUseModal = false">
  <div class="modal">
    <h2 class="modal-title">Fair Use Policy</h2>
    <div class="modal-content formatted-privacy">
      <p><strong>Last Updated:</strong> July 8, 2025</p>
 <h1>Fair Use Policy</h1>

    <p>
      This Fair Use Policy is intended to ensure that all users of the Pathtracer platform have a reliable and performant experience.
    </p>

    <h2>1. Purpose</h2>
    <p>
      The tools provided by this platform are designed for responsible human use in the context of blockchain analysis, research, and investigations.
    </p>

    <h2>2. Acceptable Use</h2>
    <ul>
      <li>Using the tools for personal, educational, or professional blockchain analysis</li>
      <li>Moderate use of batch or automated analysis features (if available)</li>
      <li>Adhering to any rate limits or usage guidelines outlined in documentation</li>
      <li>Max pending requests: 100</li>
      <li>Max request history: 150</li>
    </ul>

    <h2>3. Prohibited Behavior</h2>
    <ul>
      <li>Excessive automated requests (e.g., botting, scraping, brute force queries)</li>
      <li>Sharing access tokens or credentials with unauthorized users</li>
      <li>Using the platform to interfere with the service availability of others</li>
    </ul>

    <h2>4. Enforcement</h2>
    <p>
      If your usage is flagged as excessive or abusive — even under a subscription or unlimited access plan — your account may be subject to:
    </p>
    <ul>
      <li>Temporary throttling or request delays</li>
      <li>Manual usage review</li>
      <li>Suspension or termination of access</li>
    </ul>

    <h2>5. Need More?</h2>
    <p>
      If you believe your use case warrants higher usage thresholds, please contact support to discuss enterprise access.
    </p>

    </div>

    <button class="primary-button" @click="showFairUseModal = false">Close</button>
  </div>
</div>

  
    <div class="importantfield_pleasefillout">
      <label>Please fill this out too:</label>
      <input v-model="importantField" autocomplete="off" tabindex="-1" />
    </div>
  </div>


<!-- Scam Victim Warning Modal -->
<div
  v-if="showVictimWarning"
  class="modal-overlay"
  @click.self="onCancelVictimWarning"
>
  <div class="modal">
    <h2 class="modal-title">Important notice for scam victims</h2>

    <div class="modal-content formatted-privacy">
      <p>
        If you have lost money in a cryptocurrency scam, I am genuinely sorry.
        This message is here to be completely clear about what Pathtracer can
        and cannot do for you.
      </p>

      <h3>1. Pathtracer is not a recovery service</h3>
      <p>
        This tool cannot get your money back. Cryptocurrency transactions are
        irreversible. Pathtracer does not have any special ability to reverse
        transfers, unlock wallets, or force exchanges to return funds.
      </p>

      <h3>2. Beware “recovery” promises</h3>
      <p>
        Many companies and individuals try to lure scam victims with promises of
        fund recovery or “unlock” services. If anyone says they can recover your
        stolen crypto in exchange for fees or more crypto, they are almost
        certainly running a second scam.
      </p>

      <h3>3. This tool costs money to use</h3>
      <p>
        Pathtracer runs on a credit system and is not free. Scam victims are
        often in a very vulnerable financial position. If you have already lost
        funds, you should not feel any pressure to spend your remaining money
        on tools like this.
      </p>
      <p>
        In most cases, it is better for professionals supporting you
        &mdash; for example law enforcement, your bank, your lawyer, or a trusted
        investigator &mdash; to decide whether using this tool makes sense for
        your situation.
      </p>

      <h3>4. Tracing funds is not the same as recovery</h3>
      <p>
        Pathtracer can sometimes show where funds appear to move next, such as
        to exchanges or other wallets. That does <strong>not</strong> mean those
        funds can be recovered. Results are automated, may be incomplete or
        inaccurate, and are for informational purposes only. They are not legal,
        financial, or law-enforcement advice and are not a guarantee that any
        case will be accepted.
      </p>
<!--
      <h3>5. Stop talking to the scammer</h3>
      <p>
        If you were guided onto a scam platform and are still in contact with
        the person who “helped” you invest or trade, please stop all contact
        with them immediately. They are very likely the scammer, even if they
        blame a website or third party instead of themselves.
      </p>
-->
      <p>
        If you are unsure what to do next, consider reporting the incident to
        your local law-enforcement or cybercrime reporting channel, and speak
        with someone you trust before sending any more money or giving anyone
        access to your devices or accounts.
      </p>

     <p>
  For more information, you can read the full
  article here:
  <a
    href="/updates.php?id=pathtracer-scam-victims-expectations"
    target="_blank"
    rel="noopener"
  >
    If You Were Scammed: What PathTracer Can and Can’t Do
  </a>.
</p>
    </div>

    <label class="checkbox-inline" style="display:block; margin-top: 1rem;">
      <input type="checkbox" v-model="victimAcknowledged" />
      <span>
        I have read and understand that Pathtracer cannot recover my funds and
        is not a recovery service.
      </span>
    </label>

    <div class="modal-actions" style="margin-top: 1rem; display:flex; gap:0.5rem; justify-content:flex-end;">
      <button
        type="button"
        class="secondary-button"
        @click="onCancelVictimWarning"
      >
        Go Back
      </button>
      <button
        type="button"
        class="primary-button"
        :disabled="!victimAcknowledged"
        @click="onConfirmVictimWarning"
        data-track="signup_victim_warning_continue"
        :data-track-props="JSON.stringify({ intendedUse: formModel.intendedUse })"
      >
        Continue to Terms
      </button>
    </div>
  </div>


      <!-- COMPLETED CARD -->
      <div class="phase-card" :class="getPhaseClass('completed')">
        <div class="step-header">
          <span>✓</span>
          <label>Completed</label>
        </div>
        <div v-if="currentPhase === 'completed'">
          <p class="success-message">
            Your account has been created and you are logged in.
          </p>
          <p>
            You can now start using PathTracer. Admins still review new accounts
            regularly, and you may be contacted if we need more information.
          </p>
          <button
            class="primary-button"
            @click="$router.push({ name: 'home' })"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import AuthService from '@/services/AuthService';

import flatpickr from 'flatpickr';
import 'flatpickr/dist/flatpickr.min.css';

export default {
  name: 'SignupFlow',
 emits: ['login-success'],
  data() {
    return {
      showTermsModal: false,
      showPrivacyModal: false,
      showFairUseModal: false,

      currentPhase: 'intro',

      // Account fields
      username: '',
      password: '',
      passwordConfirm: '',

      dobPicker: null,
      formStartTime: null,

      // Honeypot
      importantField: '',

      agreedToTerms: false,
      isSubmitting: false,
      errorMessage: '',
      successMessage: '',

      showVictimWarning: false,
      victimAcknowledged: false,

      referralFromQuery: false,

      countries: [
        { code: 'CA', name: 'Canada' },
        { code: 'US', name: 'United States' },
        { code: 'MX', name: 'Mexico' },
      
  // Rest of world (A–Z by name)
  { code: 'AF', name: 'Afghanistan' },
  { code: 'AL', name: 'Albania' },
  { code: 'DZ', name: 'Algeria' },
  { code: 'AD', name: 'Andorra' },
  { code: 'AO', name: 'Angola' },
  { code: 'AG', name: 'Antigua and Barbuda' },
  { code: 'AR', name: 'Argentina' },
  { code: 'AM', name: 'Armenia' },
  { code: 'AU', name: 'Australia' },
  { code: 'AT', name: 'Austria' },
  { code: 'AZ', name: 'Azerbaijan' },

  { code: 'BS', name: 'Bahamas' },
  { code: 'BH', name: 'Bahrain' },
  { code: 'BD', name: 'Bangladesh' },
  { code: 'BB', name: 'Barbados' },
  { code: 'BY', name: 'Belarus' },
  { code: 'BE', name: 'Belgium' },
  { code: 'BZ', name: 'Belize' },
  { code: 'BJ', name: 'Benin' },
  { code: 'BT', name: 'Bhutan' },
  { code: 'BO', name: 'Bolivia' },
  { code: 'BA', name: 'Bosnia and Herzegovina' },
  { code: 'BW', name: 'Botswana' },
  { code: 'BR', name: 'Brazil' },
  { code: 'BN', name: 'Brunei' },
  { code: 'BG', name: 'Bulgaria' },
  { code: 'BF', name: 'Burkina Faso' },
  { code: 'BI', name: 'Burundi' },

  { code: 'KH', name: 'Cambodia' },
  { code: 'CM', name: 'Cameroon' },
  { code: 'CV', name: 'Cabo Verde' },
  { code: 'CF', name: 'Central African Republic' },
  { code: 'TD', name: 'Chad' },
  { code: 'CL', name: 'Chile' },
  { code: 'CN', name: 'China' },
  { code: 'CO', name: 'Colombia' },
  { code: 'KM', name: 'Comoros' },
  { code: 'CG', name: 'Congo' },
  { code: 'CD', name: 'Congo, Democratic Republic of the' },
  { code: 'CR', name: 'Costa Rica' },
  { code: 'CI', name: "Côte d'Ivoire" },
  { code: 'HR', name: 'Croatia' },
  { code: 'CU', name: 'Cuba' },
  { code: 'CY', name: 'Cyprus' },
  { code: 'CZ', name: 'Czech Republic' },

  { code: 'DK', name: 'Denmark' },
  { code: 'DJ', name: 'Djibouti' },
  { code: 'DM', name: 'Dominica' },
  { code: 'DO', name: 'Dominican Republic' },

  { code: 'EC', name: 'Ecuador' },
  { code: 'EG', name: 'Egypt' },
  { code: 'SV', name: 'El Salvador' },
  { code: 'GQ', name: 'Equatorial Guinea' },
  { code: 'ER', name: 'Eritrea' },
  { code: 'EE', name: 'Estonia' },
  { code: 'SZ', name: 'Eswatini' },
  { code: 'ET', name: 'Ethiopia' },

  { code: 'FJ', name: 'Fiji' },
  { code: 'FI', name: 'Finland' },
  { code: 'FR', name: 'France' },

  { code: 'GA', name: 'Gabon' },
  { code: 'GM', name: 'Gambia' },
  { code: 'GE', name: 'Georgia' },
  { code: 'DE', name: 'Germany' },
  { code: 'GH', name: 'Ghana' },
  { code: 'GR', name: 'Greece' },
  { code: 'GD', name: 'Grenada' },
  { code: 'GT', name: 'Guatemala' },
  { code: 'GN', name: 'Guinea' },
  { code: 'GW', name: 'Guinea-Bissau' },
  { code: 'GY', name: 'Guyana' },

  { code: 'HT', name: 'Haiti' },
  { code: 'HN', name: 'Honduras' },
  { code: 'HK', name: 'Hong Kong' },
  { code: 'HU', name: 'Hungary' },

  { code: 'IS', name: 'Iceland' },
  { code: 'IN', name: 'India' },
  { code: 'ID', name: 'Indonesia' },
  { code: 'IR', name: 'Iran' },
  { code: 'IQ', name: 'Iraq' },
  { code: 'IE', name: 'Ireland' },
  { code: 'IL', name: 'Israel' },
  { code: 'IT', name: 'Italy' },

  { code: 'JM', name: 'Jamaica' },
  { code: 'JP', name: 'Japan' },
  { code: 'JO', name: 'Jordan' },

  { code: 'KZ', name: 'Kazakhstan' },
  { code: 'KE', name: 'Kenya' },
  { code: 'KI', name: 'Kiribati' },
  { code: 'KW', name: 'Kuwait' },
  { code: 'KG', name: 'Kyrgyzstan' },

  { code: 'LA', name: 'Laos' },
  { code: 'LV', name: 'Latvia' },
  { code: 'LB', name: 'Lebanon' },
  { code: 'LS', name: 'Lesotho' },
  { code: 'LR', name: 'Liberia' },
  { code: 'LY', name: 'Libya' },
  { code: 'LI', name: 'Liechtenstein' },
  { code: 'LT', name: 'Lithuania' },
  { code: 'LU', name: 'Luxembourg' },

  { code: 'MG', name: 'Madagascar' },
  { code: 'MW', name: 'Malawi' },
  { code: 'MY', name: 'Malaysia' },
  { code: 'MV', name: 'Maldives' },
  { code: 'ML', name: 'Mali' },
  { code: 'MT', name: 'Malta' },
  { code: 'MH', name: 'Marshall Islands' },
  { code: 'MR', name: 'Mauritania' },
  { code: 'MU', name: 'Mauritius' },
  { code: 'FM', name: 'Micronesia' },
  { code: 'MD', name: 'Moldova' },
  { code: 'MC', name: 'Monaco' },
  { code: 'MN', name: 'Mongolia' },
  { code: 'ME', name: 'Montenegro' },
  { code: 'MA', name: 'Morocco' },
  { code: 'MZ', name: 'Mozambique' },
  { code: 'MM', name: 'Myanmar' },

  { code: 'NA', name: 'Namibia' },
  { code: 'NR', name: 'Nauru' },
  { code: 'NP', name: 'Nepal' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'NI', name: 'Nicaragua' },
  { code: 'NE', name: 'Niger' },
  { code: 'NG', name: 'Nigeria' },
  { code: 'KP', name: 'North Korea' },
  { code: 'MK', name: 'North Macedonia' },
  { code: 'NO', name: 'Norway' },

  { code: 'OM', name: 'Oman' },

  { code: 'PK', name: 'Pakistan' },
  { code: 'PW', name: 'Palau' },
  { code: 'PA', name: 'Panama' },
  { code: 'PG', name: 'Papua New Guinea' },
  { code: 'PY', name: 'Paraguay' },
  { code: 'PE', name: 'Peru' },
  { code: 'PH', name: 'Philippines' },
  { code: 'PL', name: 'Poland' },
  { code: 'PT', name: 'Portugal' },

  { code: 'QA', name: 'Qatar' },

  { code: 'RO', name: 'Romania' },
  { code: 'RU', name: 'Russia' },
  { code: 'RW', name: 'Rwanda' },

  { code: 'KN', name: 'Saint Kitts and Nevis' },
  { code: 'LC', name: 'Saint Lucia' },
  { code: 'VC', name: 'Saint Vincent and the Grenadines' },
  { code: 'WS', name: 'Samoa' },
  { code: 'SM', name: 'San Marino' },
  { code: 'ST', name: 'Sao Tome and Principe' },
  { code: 'SA', name: 'Saudi Arabia' },
  { code: 'SN', name: 'Senegal' },
  { code: 'RS', name: 'Serbia' },
  { code: 'SC', name: 'Seychelles' },
  { code: 'SL', name: 'Sierra Leone' },
  { code: 'SG', name: 'Singapore' },
  { code: 'SK', name: 'Slovakia' },
  { code: 'SI', name: 'Slovenia' },
  { code: 'SB', name: 'Solomon Islands' },
  { code: 'SO', name: 'Somalia' },
  { code: 'ZA', name: 'South Africa' },
  { code: 'KR', name: 'South Korea' },
  { code: 'SS', name: 'South Sudan' },
  { code: 'ES', name: 'Spain' },
  { code: 'LK', name: 'Sri Lanka' },
  { code: 'SD', name: 'Sudan' },
  { code: 'SR', name: 'Suriname' },
  { code: 'SE', name: 'Sweden' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'SY', name: 'Syria' },

  { code: 'TW', name: 'Taiwan' },
  { code: 'TJ', name: 'Tajikistan' },
  { code: 'TZ', name: 'Tanzania' },
  { code: 'TH', name: 'Thailand' },
  { code: 'TL', name: 'Timor-Leste' },
  { code: 'TG', name: 'Togo' },
  { code: 'TO', name: 'Tonga' },
  { code: 'TT', name: 'Trinidad and Tobago' },
  { code: 'TN', name: 'Tunisia' },
  { code: 'TR', name: 'Turkey' },
  { code: 'TM', name: 'Turkmenistan' },
  { code: 'TV', name: 'Tuvalu' },

  { code: 'UG', name: 'Uganda' },
  { code: 'UA', name: 'Ukraine' },
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'UY', name: 'Uruguay' },
  { code: 'UZ', name: 'Uzbekistan' },

  { code: 'VU', name: 'Vanuatu' },
  { code: 'VA', name: 'Vatican City' },
  { code: 'VE', name: 'Venezuela' },
  { code: 'VN', name: 'Vietnam' },

  { code: 'YE', name: 'Yemen' },

  { code: 'ZM', name: 'Zambia' },
  { code: 'ZW', name: 'Zimbabwe' }
      ],

      formModel: {
        firstName: '',
        lastName: '',
        email: '',
        dob: '',
        country: 'United States',
        intendedUse: '',
        lawEnforcementFlag: false,
        referralCode: '',
        promoCode: ''
      }
    };
  },

  computed: {
    formFields() {
      return [
        { label: 'First Name:', model: 'firstName', type: 'text', required: true, max: 100 },
        { label: 'Last Name:', model: 'lastName', type: 'text', required: true, max: 100 },
        { label: 'Email:', model: 'email', type: 'email', required: true, max: 255 },
        { label: 'Date of Birth:', model: 'dob', type: 'date', required: true },
        { label: 'Country of Residence:', model: 'country', type: 'text', required: true, max: 100 },
        { label: 'Intended Use (optional):', model: 'intendedUse', type: 'select', select: true }
      ];
    }
  },

  created() {
    // Redirect to home if already authenticated
    AuthService.isAuthenticated().then((isAuth) => {
      if (isAuth) {
        this.$router.push({ name: 'home' });
      }
    });
  },

  mounted() {
  this.formStartTime = performance.now();

  if (this.currentPhase === 'form') {
    this.$nextTick(() => {
      this.initDobPicker();
    });
  }

  const q = this.getQueryParams();

  // Only use actual query params for referral, not localStorage fallback
  const refCandidate = (q.ref || q.referral || '').toString().trim();
  const ref = this.sanitizeRef(refCandidate);

  this.referralFromQuery = !!ref;

  if (ref) {
    this.formModel.referralCode = ref;

    // If you still want to persist "real" referrals for later nav, keep these:
    localStorage.setItem('pt_ref', ref);
    document.cookie = `pt_ref=${encodeURIComponent(ref)}; Max-Age=${60 * 60 * 24 * 30}; Path=/; SameSite=Lax`;
  } else {
    // Optionally clear old junk like "Code"
    localStorage.removeItem('pt_ref');
  }

  const promo = (q.promo || q.code || '').toString().trim();
  if (promo) {
    this.formModel.promoCode = promo.slice(0, 64);
  }
},


  beforeUnmount() {
    if (this.dobPicker) {
      this.dobPicker.destroy();
      this.dobPicker = null;
    }
  },

  watch: {
    currentPhase(newVal, oldVal) {
      if (newVal === 'form') {
        this.$nextTick(() => {
          this.initDobPicker();
        });
      }

      if (oldVal === 'form' && newVal !== 'form' && this.dobPicker) {
        this.dobPicker.destroy();
        this.dobPicker = null;
      }
    }
  },

  methods: {
    scrollErrorIntoView() {
  this.$nextTick(() => {
    const errorEl =
      this.$el.querySelector('.global-error') ||
      this.$el.querySelector('.error-message');
    if (errorEl) {
      errorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  });
},

    // Existing victim warning handlers
    onCancelVictimWarning() {
      this.showVictimWarning = false;
      this.victimAcknowledged = false;
    },

    onConfirmVictimWarning() {
      if (!this.victimAcknowledged) return;
      this.showVictimWarning = false;
      this.currentPhase = 'tos';
    },

    initDobPicker() {
      const inputEl = this.$refs.dobInput;
      if (!inputEl) return;

      const targetEl = Array.isArray(inputEl) ? inputEl[0] : inputEl;
      if (!targetEl) return;

      if (this.dobPicker) {
        this.dobPicker.destroy();
        this.dobPicker = null;
      }

      this.dobPicker = flatpickr(targetEl, {
        enableTime: false,
        dateFormat: 'Y-m-d',
        maxDate: 'today',
        minDate: '1920-01-01',
        allowInput: true,
        defaultDate: this.formModel.dob || null,
        onReady: (_selectedDates, _dateStr, instance) => {
          const yearInput = instance.currentYearElement;
          if (!yearInput) return;

          yearInput.setAttribute('data-1p-ignore', '');
          yearInput.setAttribute('data-op-ignore', '');
          yearInput.setAttribute('autocomplete', 'off');
        },
        onChange: (selectedDates, dateStr) => {
          this.formModel.dob = dateStr;
        },
        onClose: (selectedDates, dateStr) => {
          if (dateStr) {
            this.formModel.dob = dateStr;
          }
        }
      });
    },

    getQueryParams() {
      try {
        if (this.$route?.query) return this.$route.query;
        return Object.fromEntries(new URLSearchParams(window.location.search));
      } catch {
        return {};
      }
    },

    sanitizeRef(code) {
      const c = (code || '').trim();
      return /^[A-Za-z0-9_-]{2,64}$/.test(c) ? c : '';
    },

    getPhaseClass(phase) {
      const order = ['intro', 'form', 'account', 'tos', 'completed'];
      if (this.currentPhase === phase) return 'active-phase';
      const currentIdx = order.indexOf(this.currentPhase);
      const thisIdx = order.indexOf(phase);
      return thisIdx < currentIdx ? 'complete-phase' : 'disabled-phase';
    },

    trySetPhase(phase) {
      const order = ['intro', 'form', 'account', 'tos'];
      const currentIdx = order.indexOf(this.currentPhase);
      const targetIdx = order.indexOf(phase);
      if (targetIdx < currentIdx) {
        this.currentPhase = phase;
      }
    },

    isValidEmail(email) {
      const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return pattern.test(email);
    },

    isValidUsername(name) {
      // Allow leading letter, then letters/numbers/_/-
      return /^[a-zA-Z][a-zA-Z0-9_-]{2,49}$/.test(name);
    },

    isOver18(dobStr) {
      const dob = new Date(dobStr);
      const today = new Date();
      const age = today.getFullYear() - dob.getFullYear();
      const hasHadBirthdayThisYear =
        today.getMonth() > dob.getMonth() ||
        (today.getMonth() === dob.getMonth() && today.getDate() >= dob.getDate());
      return age > 18 || (age === 18 && hasHadBirthdayThisYear);
    },

   goToAccount() {
  this.errorMessage = '';

  if (
    !this.formModel.firstName ||
    !this.formModel.lastName ||
    !this.formModel.email ||
    !this.formModel.dob ||
    !this.formModel.country
  ) {
    this.errorMessage = 'Please fill out all required fields.';
    this.scrollErrorIntoView();
    return;
  }

  if (!this.isValidEmail(this.formModel.email)) {
    this.errorMessage = 'Please enter a valid email address.';
    this.scrollErrorIntoView();
    return;
  }

  if (!this.isOver18(this.formModel.dob)) {
    this.errorMessage = 'You must be at least 18 years old to sign up.';
    this.scrollErrorIntoView();
    return;
  }

  if (this.formModel.intendedUse === 'I am the victim of a scam') {
    this.errorMessage = '';
    this.victimAcknowledged = false;
    this.showVictimWarning = true;
    return;
  }

  this.errorMessage = '';
  this.currentPhase = 'account';
},
goToTOSFromAccount() {
  this.errorMessage = '';

  if (!this.isValidUsername(this.username)) {
    this.errorMessage =
      'Invalid username. Must start with a letter and contain only letters, numbers, underscores, or hyphens.';
    this.scrollErrorIntoView();
    return;
  }

  if (this.password.length < 6) {
    this.errorMessage = 'Password must be at least 6 characters.';
    this.scrollErrorIntoView();
    return;
  }

  if (this.password !== this.passwordConfirm) {
    this.errorMessage = 'Passwords do not match.';
    this.scrollErrorIntoView();
    return;
  }

  this.errorMessage = '';
  this.currentPhase = 'tos';
},


    async submitSignup() {
      this.errorMessage = '';
      this.successMessage = '';

      // Honeypot: bots filling hidden field get a fake "success"
      if (this.importantField) {
        this.errorMessage = 'Success! Thank you for signing up.';
        return;
      }

      const formDuration = performance.now() - this.formStartTime;
      if (formDuration < 3000) {
        this.errorMessage = 'Form submitted too quickly. Please try again.';
        return;
      }

      // Final validation guardrail
      if (
        !this.formModel.firstName ||
        !this.formModel.lastName ||
        !this.formModel.email ||
        !this.formModel.dob ||
        !this.formModel.country
      ) {
        this.errorMessage = 'Please fill out all required fields.';
        return;
      }

      if (!this.isValidEmail(this.formModel.email)) {
        this.errorMessage = 'Please enter a valid email address.';
        return;
      }

      if (!this.isOver18(this.formModel.dob)) {
        this.errorMessage = 'You must be at least 18 years old to sign up.';
        return;
      }

      if (!this.isValidUsername(this.username)) {
        this.errorMessage =
          'Invalid username. Must start with a letter and contain only letters, numbers, underscores, or hyphens.';
        return;
      }

      if (this.password.length < 6) {
        this.errorMessage = 'Password must be at least 6 characters.';
        return;
      }

     if (!this.agreedToTerms) {
  this.errorMessage = 'You must agree to the Terms of Service and Privacy Policy.';
  this.scrollErrorIntoView();
  return;
}


      this.isSubmitting = true;

      try {
        const payload = {
          first_name: this.formModel.firstName,
          last_name: this.formModel.lastName,
          email: this.formModel.email,
          dob: this.formModel.dob,
          country: this.formModel.country,
          intended_use: this.formModel.intendedUse,
          law_enforcement_flag: this.formModel.lawEnforcementFlag,
          referral_code: this.formModel.referralCode,
          promo_code: this.formModel.promoCode,
          time_taken_ms: Math.floor(formDuration),
          username: this.username,
          password: this.password
        };

        const response = await axios.post('/etherscan/direct_signup.php', payload);

   if (response.data.status === 'success') {
  try {
    // Attempt auto-login
    const loginOk = await AuthService.login(this.username, this.password);

    if (loginOk) {
      // Tell App.vue to refresh navbar auth state
      this.$emit('login-success');

      // Clear fields for safety
      this.formModel = {
        firstName: '',
        lastName: '',
        email: '',
        dob: '',
        country: '',
        intendedUse: '',
        lawEnforcementFlag: false,
        referralCode: '',
        promoCode: ''
      };
      this.username = '';
      this.password = '';
      this.passwordConfirm = '';
      this.agreedToTerms = false;

      // Go straight to dashboard/home
      this.$router.push({ name: 'home' });
      return; // stop here; we’re leaving this page
    } else {
      console.error('Auto-login returned false');
      // Account exists, but login failed for some reason
      this.currentPhase = 'completed';
      this.successMessage =
        'Account created successfully. Please log in with your new credentials.';
    }
  } catch (loginErr) {
    console.error('Auto-login failed:', loginErr);
    this.currentPhase = 'completed';
    this.successMessage =
      'Account created successfully. Please log in with your new credentials.';
  }

  // Fallback path: account created but user not logged in; clear sensitive fields
  this.formModel = {
    firstName: '',
    lastName: '',
    email: '',
    dob: '',
    country: '',
    intendedUse: '',
    lawEnforcementFlag: false,
    referralCode: '',
    promoCode: ''
  };
  this.username = '';
  this.password = '';
  this.passwordConfirm = '';
  this.agreedToTerms = false;
} else {
  this.errorMessage = response.data.message || 'Signup failed.';
  this.scrollErrorIntoView();
}


      } catch (err) {
        console.error('Signup error:', err);
        this.errorMessage = 'An error occurred. Please try again later.';
      } finally {
        this.isSubmitting = false;
      }
    }
  }
};
</script>

<style scoped>
.signup-page {
  max-width: 600px;
  margin: 0 auto;
  padding: 20px;
}
.error-message {
  color: red;
}
.success-message {
  color: green;
}
.importantfield_pleasefillout {
  position: absolute;
  left: -9999px;
  top: -9999px;
  opacity: 0;
  height: 0;
  width: 0;
  overflow: hidden;
}
.signup-progress {
  display: flex;
  justify-content: space-between;
  margin-bottom: 30px;
  position: relative;
  counter-reset: step;
}

.step {
  flex: 1;
  text-align: center;
  position: relative;
  color: #aaa;
  font-size: 14px;
}

.step span {
  display: inline-block;
  width: 28px;
  height: 28px;
  line-height: 28px;
  background-color: #ccc;
  color: white;
  border-radius: 50%;
  margin-bottom: 5px;
  transition: background-color 0.3s;
}

.step.complete span {
  background-color: #2ecc71; /* green for complete */
}

.step.active span {
  background-color: #3498db; /* blue for current */
}

.step label {
  display: block;
}

.step::after {
  content: '';
  position: absolute;
  top: 14px;
  right: -50%;
  width: 100%;
  height: 2px;
  background: #ccc;
  z-index: -1;
}

.step:last-child::after {
  display: none;
}

.step.complete::after {
  background: #2ecc71;
}
.phase-card {
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
  padding: 30px;
  margin-top: 20px;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  font-weight: 600;
  margin-bottom: 6px;
  color: #444;
}

.form-group input,
.form-group select {
  width: 100%;
  padding: 10px;
  font-size: 15px;
  border: 1px solid #ccc;
  border-radius: 6px;
  transition: border-color 0.2s;
}

.form-group input:focus,
.form-group select:focus {
  outline: none;
  border-color: #3498db;
}

.primary-button {
  background-color: #3498db;
  color: white;
  padding: 12px 24px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: bold;
  font-size: 15px;
  transition: background-color 0.2s;
}

.primary-button:hover {
  background-color: #2c80b4;
}

.secondary-button {
  background-color: #ecf0f1;
  color: #333;
  padding: 12px 20px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  margin-left: 10px;
  font-size: 14px;
}

ul {
  padding-left: 18px;
}

.error-message,
.success-message {
  margin-top: 16px;
  font-weight: 500;
}

.error-message {
  color: #e74c3c;
}

.success-message {
  color: #2ecc71;
}
.phase-stack {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.phase-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
  padding: 20px;
  transition: opacity 0.3s, filter 0.3s;
}

.step-header {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
}

.step-header span {
  display: inline-block;
  width: 28px;
  height: 28px;
  background-color: #ccc;
  color: white;
  font-weight: bold;
  border-radius: 50%;
  line-height: 28px;
  text-align: center;
  margin-right: 10px;
}

.step-header label {
  font-weight: 600;
  font-size: 16px;
}

/* Phase Status Styles */
.disabled-phase {
  opacity: 0.5;
  pointer-events: none;
  filter: grayscale(50%);
}

.complete-phase .step-header span {
  background-color: #2ecc71;
}

.active-phase .step-header span {
  background-color: #3498db;
}
.spinner-with-text {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid #fff;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
.step-header.clickable {
  cursor: pointer;
}

.step-header.clickable:hover span {
  box-shadow: 0 0 0 2px #2ecc71;
  transition: box-shadow 0.2s ease;
}
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal {
  background: white;
  border-radius: 8px;
  max-width: 600px;
  width: 90%;
  padding: 20px;
  max-height: 80vh;
  overflow-y: auto;
  animation: fadeIn 0.2s ease;
}

.modal-content {
  margin-bottom: 20px;
}

.modal h2 {
  margin-top: 0;
}

.modal button {
  display: block;
  margin-left: auto;
}
.tos-buttons {
  display: flex;
  justify-content: center; /* ✅ Center horizontally */
  flex-wrap: wrap;
  gap: 12px;
  margin: 12px 0 20px;
}

.tertiary-button {
  background: transparent;
  color: #3498db;
  border: 1px solid #3498db;
  padding: 8px 16px;
  font-size: 14px;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.2s, color 0.2s, border-color 0.2s;
}

.tertiary-button:hover {
  background-color: #ecf6fb;
  border-color: #2980b9;
  color: #2980b9;
}
.formatted-tos {
  font-size: 14px;
  line-height: 1.6;
  color: #333;
}

.formatted-tos h3 {
  margin-top: 20px;
  margin-bottom: 8px;
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
}

.formatted-tos ul {
  padding-left: 20px;
  margin-top: 4px;
  margin-bottom: 12px;
}

.formatted-tos ul li {
  margin-bottom: 6px;
}

.formatted-tos a {
  color: #3498db;
  text-decoration: underline;
}

.formatted-tos a:hover {
  color: #2980b9;
}
.formatted-privacy,
.formatted-tos {
  font-size: 14px;
  line-height: 1.6;
  color: #333;
}

.formatted-privacy h3,
.formatted-tos h3 {
  margin-top: 20px;
  margin-bottom: 8px;
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
}

.formatted-privacy ul,
.formatted-tos ul {
  padding-left: 20px;
  margin-top: 4px;
  margin-bottom: 12px;
}

.formatted-privacy ul li,
.formatted-tos ul li {
  margin-bottom: 6px;
}

.formatted-privacy a,
.formatted-tos a {
  color: #3498db;
  text-decoration: underline;
}

.formatted-privacy a:hover,
.formatted-tos a:hover {
  color: #2980b9;
}
.phase-card .step-header span {
  background-color: #ccc;
}

.phase-card.complete-phase .step-header span,
.phase-card.active-phase .step-header span {
  background-color: #2ecc71;
}
.datepicker-input {
  width: 100%;
  padding: 10px;
  font-size: 15px;
  border: 1px solid #ccc;
  border-radius: 6px;
}.country-select {
  max-height: 260px;       /* safety cap for small screens */
  overflow-y: auto;        /* scroll when there are many options */
  width: 100%;             /* match other inputs */
  box-sizing: border-box;
}

/* Optional: make it visually consistent with your other inputs */
.country-select {
  padding: 6px 8px;
  border-radius: 4px;
  border: 1px solid #ccc;
  font-size: 14px;
  background-color: #fff;
}

.helper {
  display: block;
  margin-top: 4px;
  color: #666;
  font-size: 0.85em;
}

.checkbox-label {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
}

.checkbox-label input[type="checkbox"] {
  margin: 0;
}
.global-error {
  margin-bottom: 1rem;
}
/* Force the checkbox + text onto one line inside the form group */
.form-group .checkbox-label {
  display: inline-flex !important; /* override .form-group label */
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0; /* in case .form-group label added extra margin */
}

/* Make sure the children don't force a line break */
.form-group .checkbox-label input[type="checkbox"],
.form-group .checkbox-label span {
  display: inline-block;
}

</style>
